// DEM-2129 — Loops C · Lista de Loops (menu Mission Control → /loops).
//
// Admin 2026-07-20: só existe loop de AGENTE. O card mostra o NOME real do
// agente (projeção agent_name da API) e o CANAL DE SAÍDA; a lista filtra por
// agente e por canal (client-side, derivado dos loops carregados). Pausar/
// retomar inline via POST /v1/loops/:id/pause|resume. "Novo Loop" abre o wizard.

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Repeat, Plus, Play, Pause, Bot, Send, CalendarClock } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { api } from '@/lib/api';
import type { AgentListItem, LoopOutputChannel } from '@/lib/api';
import type { Loop } from '@/lib/types/loops';
import { fmtDateTime } from '@/lib/format';
import { LoopWizard } from '@/components/loops/LoopWizard';
import {
  loopScheduleHuman,
  loopStatusBadge,
  loopStatusLabel,
  loopAgentName,
  loopChannelLabel,
  runStatusBadge,
  runStatusLabel,
  orDash,
} from '@/components/loops/loops-ui';

export function LoopsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loops, setLoops] = useState<Loop[]>([]);
  // Fallback client-side (janela até o restart do backend): o backend vivo pode
  // não mandar as projeções agent_name/output_channel — resolvemos pelo
  // endpoint /channels (canais = companions com bot) e pelo catálogo de agents.
  const [channels, setChannels] = useState<LoopOutputChannel[]>([]);
  const [agents, setAgents] = useState<AgentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState<string>('all');
  const [channelFilter, setChannelFilter] = useState<string>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ls, chs, ags] = await Promise.all([
        api.listLoops(),
        api.listLoopChannels().catch(() => [] as LoopOutputChannel[]),
        api.listAgents().catch(() => [] as AgentListItem[]),
      ]);
      setLoops(ls);
      setChannels(chs);
      setAgents(ags);
    } catch (err) {
      toast({ message: err instanceof Error ? err.message : 'Falha ao carregar loops', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const togglePause = useCallback(
    async (loop: Loop) => {
      setBusyId(loop.id);
      try {
        const updated =
          loop.status === 'paused' ? await api.resumeLoop(loop.id) : await api.pauseLoop(loop.id);
        setLoops((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
      } catch (err) {
        toast({ message: err instanceof Error ? err.message : 'Falha ao alterar status', type: 'error' });
      } finally {
        setBusyId(null);
      }
    },
    [toast],
  );

  const channelById = useMemo(
    () => new Map(channels.map((c) => [c.id, { id: c.id, label: c.label, detail: c.detail ?? null }])),
    [channels],
  );
  const agentNameBySlug = useMemo(
    () => new Map(agents.filter((a) => a.name).map((a) => [a.slug, a.name])),
    [agents],
  );

  // Opções dos filtros derivadas dos loops carregados (agente por target_slug,
  // canal pelo rótulo legível — inclui "Na conversa de X" e "Sem canal").
  const agentOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of loops) map.set(l.target_slug, loopAgentName(l, agentNameBySlug));
    return Array.from(map.entries())
      .map(([slug, name]) => ({ slug, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [loops, agentNameBySlug]);

  const channelOptions = useMemo(() => {
    const set = new Set<string>();
    for (const l of loops) set.add(loopChannelLabel(l, channelById).label);
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [loops, channelById]);

  const filtered = useMemo(
    () =>
      loops.filter((l) => {
        if (agentFilter !== 'all' && l.target_slug !== agentFilter) return false;
        if (channelFilter !== 'all' && loopChannelLabel(l, channelById).label !== channelFilter) return false;
        return true;
      }),
    [loops, agentFilter, channelFilter, channelById],
  );

  const selectCls =
    'h-9 rounded-md border border-raios-border bg-raios-card px-2.5 text-sm text-raios-text focus:outline-none focus:ring-2 focus:ring-raios-accent/40';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Loops"
        description="Execuções rotineiras de agentes — agendadas por periodicidade."
        actions={
          <Button variant="brand" onClick={() => setWizardOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Novo Loop
          </Button>
        }
      />

      {!loading && loops.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Filtrar por agente"
            className={selectCls}
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
          >
            <option value="all">Agente: todos</option>
            {agentOptions.map((a) => (
              <option key={a.slug} value={a.slug}>
                {a.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Filtrar por canal de saída"
            className={selectCls}
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
          >
            <option value="all">Canal: todos</option>
            {channelOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {(agentFilter !== 'all' || channelFilter !== 'all') && (
            <span className="text-xs text-raios-muted">
              {filtered.length} de {loops.length}
            </span>
          )}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : loops.length === 0 ? (
        <EmptyState
          icon={Repeat}
          title="Nenhum loop ainda"
          description="Crie um loop para rodar um agente em intervalos definidos."
          action={{ label: 'Novo Loop', onClick: () => setWizardOpen(true) }}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Repeat}
          title="Nenhum loop com esses filtros"
          description="Ajuste os filtros de agente ou canal de saída."
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((loop) => {
            const channel = loopChannelLabel(loop, channelById);
            return (
              <Card
                key={loop.id}
                className="p-4 cursor-pointer hover:border-raios-accent/40 transition-colors"
                onClick={() => navigate(`/loops/${loop.id}`)}
                data-testid="loop-row"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-raios-accent/10">
                      <Repeat className="h-4.5 w-4.5 text-raios-accent-text" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-raios-text truncate">{loop.name}</span>
                        <StatusBadge
                          status={loopStatusBadge(loop.status)}
                          label={loopStatusLabel(loop.status)}
                        />
                      </div>
                      <div
                        className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
                        data-testid="loop-agent-channel"
                      >
                        <span className="flex items-center gap-1.5 text-raios-text-secondary">
                          <Bot className="h-3.5 w-3.5 text-raios-muted" aria-hidden="true" />
                          <span className="font-medium text-raios-text">{loopAgentName(loop, agentNameBySlug)}</span>
                        </span>
                        <span
                          className={`flex items-center gap-1.5 ${channel.muted ? 'text-raios-muted' : 'text-raios-text-secondary'}`}
                        >
                          <Send className="h-3.5 w-3.5 text-raios-muted" aria-hidden="true" />
                          {channel.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-raios-text-secondary">
                        <span className="flex items-center gap-1.5">
                          <CalendarClock className="h-3.5 w-3.5 text-raios-muted" aria-hidden="true" />
                          {loopScheduleHuman(loop)}
                        </span>
                        <span>
                          Próxima:{' '}
                          {loop.status === 'paused'
                            ? 'pausado (recalcula ao retomar)'
                            : orDash(loop.next_run_at ? fmtDateTime(loop.next_run_at) : null)}
                        </span>
                        <span className="flex items-center gap-1.5">
                          Última:
                          {loop.last_run ? (
                            <>
                              <StatusBadge
                                status={runStatusBadge(loop.last_run.status)}
                                label={runStatusLabel(loop.last_run.status)}
                              />
                              {orDash(
                                loop.last_run.finished_at
                                  ? fmtDateTime(loop.last_run.finished_at)
                                  : fmtDateTime(loop.last_run.scheduled_for),
                              )}
                            </>
                          ) : (
                            <span>—</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                    {loop.status !== 'archived' && loop.status !== 'completed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        loading={busyId === loop.id}
                        onClick={() => togglePause(loop)}
                        aria-label={loop.status === 'paused' ? 'Retomar' : 'Pausar'}
                      >
                        {loop.status === 'paused' ? (
                          <>
                            <Play className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Retomar
                          </>
                        ) : (
                          <>
                            <Pause className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Pausar
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <LoopWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onSaved={() => {
          setWizardOpen(false);
          void load();
        }}
      />
    </div>
  );
}
